import React, { useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert } from 'reactstrap';
import useInventoryFetch from './useInventoryFetch';

/**
 * Runs the CMDB inventory scan for a managed product.
 *
 * Opens PAUSED: reaching into a customer's ServiceNow must never happen as a
 * side effect of picking something from a dropdown, so nothing starts until
 * the user clicks Scan fleet.
 */
const InventoryFetchModal = ({ isOpen, managedProductId, managedProductName, onComplete, onCancel }) => {
  const { status, result, error, start, cancel, reset } = useInventoryFetch();
  const completedRef = useRef(false);
  const wasOpenRef = useRef(isOpen);

  // Reopening a mounted instance (e.g. the parent toggles isOpen instead of
  // unmounting, as ExportModal's caller does) must land back in the ready
  // state, not stuck on a previous scan's outcome. Only react to the
  // false -> true transition; do NOT start a scan here.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      completedRef.current = false;
      reset();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset]);

  useEffect(() => {
    if (status === 'succeeded' && result && !completedRef.current) {
      completedRef.current = true;
      onComplete(result);
    }
  }, [status, result, onComplete]);

  const handleCancel = () => {
    cancel();
    onCancel();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="lg" backdrop="static">
      <ModalHeader toggle={handleCancel}>Scan your {managedProductName} fleet</ModalHeader>
      <ModalBody>
        {status === 'idle' && (
          <>
            <p>
              BugZero will read your ServiceNow CMDB to find which software versions this fleet is
              running, and use the most common one as your project baseline.
            </p>
            <p className="text-muted mb-0">
              <em>Read-only &mdash; nothing in ServiceNow is modified.</em>
            </p>
          </>
        )}
        {status === 'scanning' && (
          <div className="text-center my-4">
            <Spinner color="primary" />
            <p className="mt-3 mb-0">Scanning your fleet&hellip;</p>
            <p className="text-muted">Reading configuration items from ServiceNow.</p>
          </div>
        )}
        {status === 'failed' && <Alert color="danger">{error}</Alert>}
      </ModalBody>
      <ModalFooter>
        {status === 'idle' && (
          <Button color="primary" onClick={() => start(managedProductId)}>
            Scan fleet
          </Button>
        )}
        {status === 'failed' && (
          <Button color="primary" onClick={() => start(managedProductId)}>
            Try again
          </Button>
        )}
        <Button color="secondary" outline onClick={handleCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InventoryFetchModal;
